import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Card,
    CardContent,
    CardActions,
    Button,
    Typography,
    Grid,
    Container,
    CircularProgress,
    Chip,
    Box,
    Snackbar,
    Alert
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPoll } from "@fortawesome/free-solid-svg-icons";
import { Survey, getActiveSurveys, addSurveyClick } from "@services/survey.service";
import SectionTitle from "@others/title-section.layout";

const SurveysListPage: React.FC = () => {
    const [surveys, setSurveys] = useState<Survey[]>([]);
    const [loading, setLoading] = useState(true);
    const [clickingId, setClickingId] = useState<number | null>(null);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error';
    }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const navigate = useNavigate();

    useEffect(() => {
        const fetchSurveys = async () => {
            try {
                const data = await getActiveSurveys();
                setSurveys(data);
            } catch (error) {
                console.error("Erreur lors du chargement des enquêtes :", error);
                setSnackbar({
                    open: true,
                    message: 'Erreur lors du chargement des enquêtes',
                    severity: 'error'
                });
            } finally {
                setLoading(false);
            }
        };
        fetchSurveys();
    }, []);

    const handleParticipate = async (survey: Survey) => {
        setClickingId(survey.id);

        try {
            // Enregistrer le clic
            await addSurveyClick(survey.id);

            // Ouvrir l'URL de l'enquête dans un nouvel onglet
            //window.open(survey.url, '_blank', 'noopener,noreferrer');

            setSnackbar({
                open: true,
                message: 'Merci pour votre participation !',
                severity: 'success'
            });
        } catch (error) {
            console.error("Erreur lors de l'enregistrement du clic :", error);
            setSnackbar({
                open: true,
                message: 'Une erreur est survenue, mais vous pouvez toujours participer',
                severity: 'error'
            });
            // Ouvrir quand même l'URL en cas d'erreur
            //window.open(survey.url, '_blank', 'noopener,noreferrer');
        } finally {
            setClickingId(null);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading) {
        return (
            <Container sx={{ textAlign: "center", mt: 4 }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <>
            <Container sx={{ pb: 5 }}>
                <SectionTitle title={"ESPACE ÉCOUTE DES HABITANTS"} />
                <Box mt={4}>
                    {surveys.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography variant="h6" color="text.secondary">
                                Aucune enquête active pour le moment.
                            </Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={4}>
                            {surveys.map((survey) => (
                                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={survey.id}>
                                    <Card
                                        sx={{
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            borderRadius: 3,
                                            boxShadow: 4,
                                            "&:hover": { boxShadow: 8 },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                bgcolor: "#f0f8fa",
                                                py: 3,
                                                borderTopLeftRadius: 12,
                                                borderTopRightRadius: 12,
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faPoll} size="2x" style={{ color: "#0288d1" }} />
                                        </Box>
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <Typography variant="h6" fontWeight={700} gutterBottom color="primary">
                                                {survey.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" mb={1}>
                                                {survey.description.length > 100
                                                    ? survey.description.substring(0, 100) + "…"
                                                    : survey.description}
                                            </Typography>
                                            <Chip
                                                color="primary"
                                                label={
                                                    "Publié le " +
                                                    new Date(survey.createdAt).toLocaleDateString("fr-FR")
                                                }
                                                sx={{ boxShadow: 2, mt: 2 }}
                                            />
                                        </CardContent>
                                        <CardActions sx={{ mt: "auto", p: 2, pt: 0 }}>
                                            <Button
                                                component="a"
                                                href={survey.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                size="large"
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                disabled={clickingId === survey.id}
                                                sx={{ fontWeight: 600, borderRadius: 2, textTransform: "none" }}
                                                onClick={() => handleParticipate(survey)}
                                            >
                                                {clickingId === survey.id ? (
                                                    <CircularProgress size={24} color="inherit" />
                                                ) : (
                                                    'Je participe !'
                                                )}
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            </Container>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default SurveysListPage;